// Joana's i18n layer (ADR-0028).
//
// Track C had NO i18n layer at all before this — ADR-0028 D7 says so in as many
// words, and the standing rule is that "Android works" is not done and the web
// has no credential excuse to hide behind. This is that layer.
//
// ## The rule is the same rule
//
// `resolveLanguage` is the TypeScript twin of the shared module's
// `InterfaceLanguage.resolve` (Kotlin,
// `mobile/shared/.../domain/InterfaceLanguage.kt`). It is a second
// implementation of one decision, which is a drift risk, so `i18n.test.ts`
// mirrors that file's test cases one for one and names it. If you change the
// rule in one, the other's tests are where you find out.
//
// The alternative — generating the function from `i18n/languages.json` the way
// the CATALOGUE is generated — was considered and refused: data belongs in a
// generator, and a control flow generated into three languages is a thing
// nobody can read in any of them.
//
// ## English is the key
//
// `t("Language")` looks the string up and falls back to the key, which IS the
// English sentence. So a missing translation renders as English and never as a
// raw identifier — ADR-0028 D4's runtime rule — and the base catalogue's job is
// to be the definitive LIST that `scripts/i18n-check.mjs` measures other
// locales against.

import { BASE_LANGUAGE, CATALOGUES, LANGUAGES, SHIPPING_LANGUAGES, type Catalogue, type Language } from "./catalogue.ts";

export { BASE_LANGUAGE, CATALOGUES, LANGUAGES, SHIPPING_LANGUAGES };
export type { Catalogue, Language };

/** Where this browser's copy of the choice lives. The ACCOUNT owns the
 *  setting (D3); this is the cache that lets the first paint be right. */
export const LANGUAGE_KEY = "language.ui";

/** Whether a tag names a language this build may OFFER. Never `LANGUAGES` —
 *  offering a half-translated locale is exactly what D4 forbids. */
export function isOfferable(tag: string | null | undefined): boolean {
  return SHIPPING_LANGUAGES.some((l) => l.tag === tag);
}

/** Read a stored tag. One this build no longer ships reads as null — "follow
 *  my browser" — rather than as a language nothing can draw. */
export function decodeLanguage(stored: string | null | undefined): string | null {
  return stored && stored.trim() !== "" && isOfferable(stored) ? stored : null;
}

/**
 * The language to draw, given the account's choice and what the browser says
 * it prefers.
 *
 * `deviceTags` is `navigator.languages` — the whole preferred list,
 * most-preferred first. Region is dropped when matching, so `sw-TZ`, `sw-KE`
 * and `sw` are one answer: we ship a language, not a country's dialect of it.
 *
 * Kept in step with `InterfaceLanguage.resolve` in the shared Kotlin module.
 */
export function resolveLanguage(chosen: string | null | undefined, deviceTags: readonly string[]): string {
  const fromChoice = decodeLanguage(chosen);
  if (fromChoice) return fromChoice;

  for (const raw of deviceTags) {
    const normalised = raw.replace(/_/g, "-").trim();
    if (normalised === "") continue;
    // Exact first, then the primary subtag, so a future regional locale is
    // matched exactly before it falls back to its base language.
    if (isOfferable(normalised)) return normalised;
    const primary = normalised.split("-")[0];
    if (isOfferable(primary)) return primary;
  }
  return BASE_LANGUAGE;
}

/** The browser's preferred languages, most-preferred first. */
export function browserTags(): readonly string[] {
  const nav = typeof navigator === "undefined" ? undefined : navigator;
  if (!nav) return [];
  return nav.languages && nav.languages.length > 0 ? nav.languages : nav.language ? [nav.language] : [];
}

/**
 * The translator for a HOST SHEET — a surface drawn outside the shell's tree.
 *
 * The payment sheet, the camera, the avatar editor and the text prompt are
 * real DOM rather than DSL nodes, so they never pass through `buildTree` and
 * never see the state that carries the account's choice. They read the browser
 * cache of it instead, which the store writes on every profile load.
 *
 * Read PER PRESENTATION and not once at module load: a language chosen
 * mid-session has to reach the next sheet, and a module-level const is built
 * before anybody has chosen anything.
 */
export function hostTranslator(): (key: string, ...args: (string | number)[]) => string {
  let stored: string | null = null;
  try {
    stored = globalThis.localStorage?.getItem(LANGUAGE_KEY) ?? null;
  } catch {
    // A browser with site data blocked answers by throwing. English, then.
  }
  return translator(resolveLanguage(decodeLanguage(stored), browserTags()));
}

/**
 * The translator for the language currently being drawn.
 *
 * `%s` placeholders are filled positionally, in order, which matches the
 * Android catalogue's `%1$s` after the index is applied — the shared
 * completeness gate checks both against the base, so a translation that drops
 * one fails CI rather than rendering a sentence with a hole in it.
 */
export function translator(tag: string): (key: string, ...args: (string | number)[]) => string {
  const table = CATALOGUES[tag] ?? CATALOGUES[BASE_LANGUAGE] ?? {};
  return (key, ...args) => {
    // The key IS the English text, so an absent entry is already correct
    // English rather than a raw identifier.
    const template = table[key] ?? key;
    if (args.length === 0) return template;
    let i = 0;
    return template.replace(/%s/g, () => String(args[i++] ?? ""));
  };
}
